import { RESERVATION_VEHICLE_LIMIT, type Vehicle } from '@repo/domain';
import { PlusIcon, XIcon } from '@repo/icons';
import { tr } from '@repo/locale';
import type { TestId } from '@repo/test-id';
import { Button, InfoCard } from '@repo/ui';
import { VehicleCheckboxGroup } from '@repo/entities';

// The backend never lets the last vehicle leave the reservation.
const MIN_RESERVATION_VEHICLES = 1;

interface Props {
  testId: TestId;
  vehicles: Vehicle[];
  selectedVehicleIds: string[];
  disabled: boolean;
  onVehicleAdd: (vehicleId: string) => void;
  onVehicleRemove: (vehicleId: string) => void;
  onClose: () => void;
  onAddNewVehicle: () => void;
}

export default function VehicleSelectionListingModalContents({
  testId,
  vehicles,
  selectedVehicleIds,
  disabled,
  onVehicleAdd,
  onVehicleRemove,
  onClose,
  onAddNewVehicle,
}: Props) {
  const handleSelectionChange = (vehicleIds: string[]) => {
    const addedVehicleId = vehicleIds.find((vehicleId) => !selectedVehicleIds.includes(vehicleId));
    if (addedVehicleId) {
      onVehicleAdd(addedVehicleId);
      return;
    }
    const removedVehicleId = selectedVehicleIds.find((vehicleId) => !vehicleIds.includes(vehicleId));
    if (removedVehicleId) onVehicleRemove(removedVehicleId);
  };

  return (
    <div className="flex max-h-[75vh] flex-col gap-4 pt-4 pb-6">
      <div className="text-heading flex flex-row items-center justify-between gap-3 pb-1">
        <div className="flex flex-row items-baseline gap-2">
          <h2 className="text-xl font-semibold">{tr('reservations:summary.vehicle_selection.title')}</h2>
          <span className="text-body text-sm" data-testid={testId.append('counter')}>
            {tr('reservations:summary.vehicle_selection.counter', {
              count: selectedVehicleIds.length,
              max: RESERVATION_VEHICLE_LIMIT,
            })}
          </span>
        </div>
        <button className="cursor-pointer" onClick={onClose} data-testid={testId.append('close')}>
          <XIcon size={20} />
        </button>
      </div>
      <InfoCard color="brand" testId={testId.append('info')}>
        <div className="flex flex-col gap-2 text-sm">
          <p>{tr('reservations:summary.vehicle_selection.info.limit', { max: RESERVATION_VEHICLE_LIMIT })}</p>
          <p>{tr('reservations:summary.vehicle_selection.info.free_time')}</p>
        </div>
      </InfoCard>
      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto p-0.5">
        <VehicleCheckboxGroup
          testId={testId.append('vehicles')}
          vehicles={vehicles}
          value={selectedVehicleIds}
          onChange={handleSelectionChange}
          disabled={disabled}
          maxSelected={RESERVATION_VEHICLE_LIMIT}
          minSelected={MIN_RESERVATION_VEHICLES}
        />
      </div>
      <Button
        testId={testId.append('addNewVehicle')}
        color="ghost"
        size="lg"
        label={tr('reservations:summary.vehicle_selection.add_new_vehicle')}
        rightIcon={PlusIcon}
        onClick={onAddNewVehicle}
        className="text-brand-strong!"
      />
    </div>
  );
}
